<template>
  <b-nav-item-dropdown
    right
    toggle-class="d-flex align-items-center dropdown-user-link p-0"
    ref="userDropdown"
    class="dropdown-user"
    menu-class="custom-user-dropdown-menu"
    @show="getMe"
  >
    <template v-if="user" #button-content>
      <div class="user-pill-trigger d-flex align-items-center">
        <b-avatar
          v-if="user.profile"
          size="36"
          :src="profileImage"
          variant="light-primary"
          class="badge-minimal mr-50 user-trigger-avatar"
          badge
          :badge-variant="userOnlineStatus === 'online' ? 'success' : 'secondary'"
        >
          <feather-icon
            v-if="!user.profile.image"
            icon="UserIcon"
            size="20"
          />
        </b-avatar>
        <span class="user-display-name font-weight-bolder mb-0 d-none d-lg-inline">
          {{ user.fullName || user.userName }}
        </span>
        <feather-icon
          icon="ChevronDownIcon"
          size="15"
          class="chevron-down-arrow ml-25"
        />
      </div>
    </template>

    <div class="dropdown-user-card" @click.stop="onCardClick">
      <!-- Top pointer caret arrow -->
      <div class="dropdown-caret-arrow"></div>

      <!-- User Header Row -->
      <div class="user-dropdown-header">
        <b-avatar
          size="50"
          :src="profileImage"
          variant="light-primary"
          class="badge-minimal header-big-avatar"
          badge
          :badge-variant="userOnlineStatus === 'online' ? 'success' : 'secondary'"
        >
          <feather-icon
            v-if="!user || !user.profile || !user.profile.image"
            icon="UserIcon"
            size="26"
          />
        </b-avatar>
        <div class="user-meta">
          <span class="user-name-title">{{ user ? (user.fullName || user.userName) : '' }}</span>
          <!-- Kredi (Elmas) bakiyesi -->
          <router-link
            :to="{ name: 'market' }"
            class="user-credit-pill"
            :title="$t('Market')"
          >
            <b-img
              :src="iconDiamond"
              width="16"
              height="14"
              alt=""
              class="credit-icon"
            />
            <span class="credit-amount">{{ formattedCredits }}</span>
            <span class="credit-label">{{ $t('Diamonds') }}</span>
            <span class="credit-plus">
              <feather-icon
                icon="PlusIcon"
                size="12"
              />
            </span>
          </router-link>
        </div>
      </div>

      <!-- Action items list -->
      <div class="dropdown-actions-list">
        <!-- 1. Hesabım -->
        <router-link
          :to="{ name: 'profile.index' }"
          class="dropdown-row-btn"
        >
          <div class="row-left">
            <svg class="row-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span class="row-text">{{ $t('Profile') || 'Hesabım' }}</span>
          </div>
        </router-link>

        <!-- Beğendiğim (takip ettiğim) kullanıcılar -->
        <router-link
          :to="{ name: 'interaction.likes' }"
          class="dropdown-row-btn"
          :class="{ active: currentRouteName === 'interaction.likes' }"
        >
          <div class="row-left">
            <svg class="row-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <span class="row-text">{{ $t('Users I like') }}</span>
          </div>
        </router-link>

        <!-- Kadın kullanıcılar: Hesap Durumu ve Görevler & Ödüller (eski yan menüdeki koşulla aynı) -->
        <template v-if="showEarningLinks">
          <router-link
            :to="{ name: 'account-status' }"
            class="dropdown-row-btn"
            :class="{ active: currentRouteName === 'account-status' }"
          >
            <div class="row-left">
              <svg class="row-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
              <span class="row-text">{{ $t('Account Status') }}</span>
            </div>
          </router-link>

          <router-link
            :to="{ name: 'rewards' }"
            class="dropdown-row-btn"
            :class="{ active: currentRouteName === 'rewards' || currentRouteName === 'tasks' }"
          >
            <div class="row-left">
              <svg class="row-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 12 20 22 4 22 4 12"></polyline>
                <rect x="2" y="7" width="20" height="5"></rect>
                <line x1="12" y1="22" x2="12" y2="7"></line>
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
              </svg>
              <span class="row-text">{{ $t('Tasks & Rewards') }}</span>
            </div>
          </router-link>
        </template>

        <!-- 2. Dil -->
        <div class="dropdown-row-btn" @click="showLanguageMenu = !showLanguageMenu">
          <div class="row-left">
            <b-img
              v-if="currentLocale && currentLocale.img"
              :src="currentLocale.img"
              class="flag-icon"
              width="20"
              height="14"
            />
            <span v-else class="flag-fallback">🇹🇷</span>
            <span class="row-text">{{ $t('Language') || 'Dil' }}</span>
          </div>
          <div class="row-right">
            <span class="row-extra-text">{{ currentLocale ? currentLocale.name : 'Türkçe' }}</span>
            <feather-icon
              :icon="showLanguageMenu ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="14"
              class="row-arrow"
            />
          </div>
        </div>

        <!-- Language Dropdown Submenu -->
        <transition name="fade">
          <div v-if="showLanguageMenu" class="sub-language-dropdown">
            <div
              v-for="l in locales"
              :key="l.locale"
              class="sub-lang-row"
              :class="{ active: $i18n.locale === l.locale }"
              @click.stop="updateLocale(l.locale)"
            >
              <b-img :src="l.img" width="18" height="12" class="mr-50" />
              <span class="sub-lang-name">{{ l.name }}</span>
              <feather-icon
                v-if="$i18n.locale === l.locale"
                icon="CheckIcon"
                size="14"
                class="ml-auto text-success"
              />
            </div>
          </div>
        </transition>

        <!-- 3. Online Durumu (Working Toggle Switch) -->
        <!-- Creator (kadın) hesaplar online durumunu elle değiştiremez; eski UpdateStatus bileşeninde de gizliydi -->
        <div v-if="showOnlineToggle" class="dropdown-row-btn online-toggle-row" @click="toggleOnlineStatus">
          <div class="row-left">
            <!-- Sun / Status Icon matching screenshot -->
            <svg class="row-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
            <span class="row-text">{{ $t('Online Status') || 'Online Durumu' }}</span>
          </div>
          <div class="row-right">
            <!-- Working Toggle Switch -->
            <div
              class="custom-nav-switch"
              :class="{ 'active': onlineStatus, 'loading': statusLoading }"
            >
              <div class="switch-track">
                <div class="switch-thumb"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. Bildirim Ayarları -->
        <router-link
          :to="{ name: 'settings.notifications' }"
          class="dropdown-row-btn"
        >
          <div class="row-left">
            <svg class="row-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            <span class="row-text">{{ $t('Notification Settings') || 'Bildirim Ayarları' }}</span>
          </div>
        </router-link>

        <!-- 5. Oturumu Kapat -->
        <div
          class="dropdown-row-btn logout-row"
          @click="logout"
        >
          <div class="row-left">
            <svg class="row-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span class="row-text">{{ $t('Logout') || 'Oturumu Kapat' }}</span>
          </div>
        </div>
      </div>
    </div>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BAvatar, BImg,
} from 'bootstrap-vue'
import {
  logout, gtmTrackNewEvent, numberFormat, throwDefaultError,
} from '@/helpers/helpers'
import useJwt from '@/auth/jwt/useJwt'
import { locales } from '@/libs/i18n/all-locales'
import { localeChanged, localize } from 'vee-validate'

export default {
  components: {
    BNavItemDropdown,
    BAvatar,
    BImg,
  },
  data() {
    return {
      iconDiamond: require('@/assets/images/icons/diamonds-1.svg'),
      locales,
      showLanguageMenu: false,
      onlineStatus: false,
      userCanChangeOnlineStatus: true,
      statusLoading: false,
    }
  },
  created() {
    this.getMe()
  },
  computed: {
    currentRouteName() {
      return this.$route.name
    },
    formattedCredits() {
      const credits = Number(this.$store.getters['auth/credits'])
      return numberFormat(Number.isFinite(credits) ? Math.floor(credits) : 0)
    },
    profileImage() {
      return this.$store.getters['auth/profileImage']
    },
    user() {
      return this.$store.getters['auth/user']
    },
    userOnlineStatus() {
      return this.$store.getters['auth/userOnlineStatus']
    },
    // Eski yan menüdeki kuralın aynısı: kadın + freeCallUser hesaplar görür
    showOnlineToggle() {
      return this.$store.getters['auth/userGender'] !== 'female'
    },
    showEarningLinks() {
      return this.$store.getters['auth/userGender'] === 'female' && Boolean(this.$store.getters['auth/freeCallUser'])
    },
    currentLocale() {
      return this.locales.find(l => l.locale === this.$i18n.locale) || this.locales[1]
    },
  },
  watch: {
    userOnlineStatus: {
      immediate: true,
      handler(val) {
        this.onlineStatus = val === 'online'
      },
    },
  },
  methods: {
    closeMenu() {
      this.showLanguageMenu = false
      if (this.$refs.userDropdown) this.$refs.userDropdown.hide()
    },
    // Menüdeki bir linke tıklanınca (aynı sayfada olsa bile) menüyü kapat
    onCardClick(event) {
      if (event.target.closest('a')) this.closeMenu()
    },
    getMe() {
      useJwt.getMe()
        .then(response => {
          const u = response.data.body
          if (u) {
            this.onlineStatus = (u.profile && u.profile.onlineStatus === 'online')
            this.userCanChangeOnlineStatus = u.canChangeOnlineStatus !== false
            this.$store.dispatch('auth/updateUser', u)
            if (u.profile && u.profile.onlineStatus) {
              this.$store.dispatch('auth/setUserOnlineStatus', u.profile.onlineStatus)
            }
          }
        })
        .catch(() => {})
    },
    toggleOnlineStatus() {
      if (this.statusLoading) return
      if (!this.userCanChangeOnlineStatus) {
        this.$bvToast.toast(this.$t('canChangeOnlineStatusText') || 'Online durumunu değiştiremezsiniz.', {
          title: this.$t('Online Status') || 'Online Durumu',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const nextStatus = this.onlineStatus ? 'offline' : 'online'
      this.statusLoading = true
      const previousStatus = this.onlineStatus
      this.onlineStatus = !this.onlineStatus

      useJwt.updateOnlineStatus(nextStatus)
        .then(response => {
          const status = response.data.body && response.data.body.onlineStatus ? response.data.body.onlineStatus : nextStatus
          this.onlineStatus = status === 'online'
          this.$store.dispatch('auth/setUserOnlineStatus', status)
          this.getMe()
          gtmTrackNewEvent('changeonlinestatus', {
            category: 'Hesap',
            action: 'ChangeOnlineStatus',
            label: this.onlineStatus ? 'Online' : 'Offline',
          })
        })
        .catch(error => {
          this.onlineStatus = previousStatus
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.statusLoading = false
        })
    },
    updateLocale(locale) {
      this.$i18n.locale = locale
      localize(locale)
      localeChanged()
      localStorage.setItem('locale', locale)

      if (locale === 'ar') {
        localStorage.setItem('isRTL', 'on')
      }
      this.$store.commit('appConfig/TOGGLE_RTL', locale === 'ar')

      if (this.$store.getters['auth/isAuthenticated']) {
        useJwt.updateLanguage(locale).catch(() => {})
      }
      this.showLanguageMenu = false
      this.closeMenu()
    },
    logout() {
      logout(this.$ability, this.$router, this.$store)

      try {
        this.$store.dispatch('socket/disconnectConnection')
        this.$store.dispatch('socket/disconnectChatConnection')
      } catch (error) {
        console.log('error', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dropdown-user {
  ::v-deep .dropdown-toggle {
    padding: 0 !important;
    &::after {
      display: none !important;
    }
  }

  ::v-deep .dropdown-menu {
    background: #18132b !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 16px !important;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.65) !important;
    padding: 12px 10px !important;
    min-width: 250px !important;
    width: 260px !important;
    top: calc(100% + 10px) !important;
    right: 0 !important;
    left: auto !important;
    overflow: visible !important;

    /* Mobilde menü ekranın sağından taşmasın: ekrana sabitle */
    @media (max-width: 767px) {
      position: fixed !important;
      top: 68px !important;
      right: 8px !important;
      left: auto !important;
      transform: none !important;
      width: min(300px, calc(100vw - 16px)) !important;
      min-width: 0 !important;
    }
  }

  .user-pill-trigger {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    padding: 3px 8px;
    border-radius: 9999px;
    transition: all 0.2s ease;

    .user-trigger-avatar {
      border: 2px solid #ea03b2 !important;
      box-shadow: 0 0 10px rgba(234, 3, 178, 0.55);
      border-radius: 50%;
      flex-shrink: 0;
      transition: all 0.2s ease;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.08);

      .user-trigger-avatar {
        box-shadow: 0 0 14px rgba(234, 3, 178, 0.85);
      }
    }

    .user-display-name {
      color: #ffffff;
      font-size: 13.5px;
      font-weight: 700;
      margin-left: 6px;
      margin-right: 4px;
    }

    .chevron-down-arrow {
      color: #94a3b8;
      stroke-width: 2.2;
    }
  }
}

.dropdown-user-card {
  position: relative;

  .dropdown-caret-arrow {
    position: absolute;
    top: -19px;
    right: 20px;

    @media (max-width: 767px) {
      right: 34px;
    }

    width: 14px;
    height: 14px;
    background: #18132b;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    transform: rotate(45deg);
    z-index: 5;
  }

  .user-dropdown-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 6px 12px 6px;

    .header-big-avatar {
      border: 2.5px solid #ea03b2 !important;
      box-shadow: 0 0 14px rgba(234, 3, 178, 0.6);
      border-radius: 50%;
      flex-shrink: 0;
    }

    .user-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      min-width: 0;
    }

    .user-name-title {
      color: #ffffff;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: -0.2px;
      line-height: 18px;
      max-width: 170px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Menübardaki seçili sekmeyle aynı gradyan */
    .user-credit-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 4px 4px 10px;
      border-radius: 9999px;
      text-decoration: none;
      background: linear-gradient(135deg, rgba(234, 3, 178, 0.28) 0%, rgba(168, 85, 247, 0.22) 50%, rgba(99, 102, 241, 0.12) 100%);
      border: 1px solid rgba(234, 3, 178, 0.35);
      box-shadow: 0 2px 14px rgba(234, 3, 178, 0.18), inset 0 1px 1px rgba(255, 255, 255, 0.2);
      transition: filter .15s ease, transform .15s ease;

      &:hover {
        filter: brightness(1.18);
        transform: translateY(-1px);
        text-decoration: none;
      }

      .credit-icon {
        filter: drop-shadow(0 0 4px rgba(234, 3, 178, 0.6));
        flex-shrink: 0;
      }

      .credit-amount {
        color: #ffffff;
        font-size: 14px;
        font-weight: 800;
        line-height: 16px;
      }

      .credit-label {
        color: rgba(236, 232, 255, 0.75);
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
      }

      .credit-plus {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        background: linear-gradient(135deg, #ea03b2, #a855f7);
        margin-left: 2px;
      }
    }
  }

  .dropdown-actions-list {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .dropdown-row-btn {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 10px;
      border-radius: 10px;
      color: #ffffff;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.18s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.08);
      }

      .row-left {
        display: flex;
        align-items: center;
        gap: 12px;

        .row-icon {
          color: #ffffff;
          stroke: #ffffff;
          flex-shrink: 0;
        }

        .flag-icon {
          border-radius: 3px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .flag-fallback {
          font-size: 16px;
          line-height: 1;
        }

        .row-text {
          font-size: 14px;
          font-weight: 500;
          color: #f1f5f9;
        }
      }

      .row-right {
        display: flex;
        align-items: center;
        gap: 6px;

        .row-extra-text {
          font-size: 13.5px;
          color: #94a3b8;
          font-weight: 500;
        }

        .row-arrow {
          color: #64748b;
        }
      }

      &.logout-row {
        margin-top: 4px;
        .row-icon, .row-text {
          color: #cbd5e1;
          stroke: #cbd5e1;
        }
        &:hover {
          background: rgba(239, 68, 68, 0.12);
          .row-icon, .row-text {
            color: #f87171;
            stroke: #f87171;
          }
        }
      }
    }
  }

  .sub-language-dropdown {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    padding: 6px;
    margin: 4px 0 6px 0;
    // 9 dilin hepsi (Arapça en sonda) kaydırmadan görünsün
    max-height: 320px;
    overflow-y: auto;

    .sub-lang-row {
      display: flex;
      align-items: center;
      padding: 6px 10px;
      border-radius: 6px;
      font-size: 13px;
      color: #e2e8f0;
      cursor: pointer;
      transition: background 0.15s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }

      &.active {
        color: #10b981;
        font-weight: 600;
      }
    }
  }
}

.custom-nav-switch {
  width: 40px;
  height: 22px;
  cursor: pointer;

  .switch-track {
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.22);
    border-radius: 9999px;
    padding: 2.5px;
    display: flex;
    align-items: center;
    transition: background-color 0.22s ease;

    .switch-thumb {
      width: 17px;
      height: 17px;
      background: #ffffff;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
      transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
      transform: translateX(0);
    }
  }

  &.active .switch-track {
    background: #10b981;

    .switch-thumb {
      transform: translateX(18px);
    }
  }

  &.loading {
    opacity: 0.6;
    pointer-events: none;
  }
}
</style>
